import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, TrendingUp } from 'lucide-react';

const PortfolioSection = ({ projects }) => {
  if (!projects || projects.length === 0) return null;

  return (
    <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-grey via-white to-grey">
      <div className="max-w-8xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
            <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">
              Featured Projects
            </span>
          </h2>
          <p className="text-xl text-dark-grey max-w-3xl mx-auto">
            Real results from real clients. Explore our successful project portfolio.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-14 h-14 rounded-full bg-white flex items-center justify-center cursor-pointer shadow-xl"
                  >
                    <ExternalLink size={24} className="text-black" />
                  </motion.div>
                </div>
              </div>

              {/* Project Details */}
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3 group-hover:text-light-pink transition-colors">
                  {project.title}
                </h3>
                <p className="text-dark-grey mb-4 line-clamp-2">
                  {project.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-grey text-xs font-mono uppercase tracking-wider text-dark-grey rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Metrics */}
                {project.metrics && (
                  <div className="border-t border-grey pt-4">
                    <div className="flex items-center gap-2 text-mid-purple mb-2">
                      <TrendingUp size={16} />
                      <span className="text-xs font-mono uppercase tracking-wider font-bold">
                        Key Metrics
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(project.metrics).map(([key, value], idx) => (
                        <div key={idx} className="text-center">
                          <div className="text-sm font-black text-black">{value}</div>
                          <div className="text-xs text-dark-grey capitalize">{key}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Decorative Corner */}
              <div className="absolute top-4 right-4 w-12 h-12 bg-light-pink/20 rounded-full blur-xl" />
            </motion.div>
          ))}
        </div>

        {/* View All Projects CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-4 rounded-full bg-gradient-to-r from-mid-purple to-light-pink text-white font-mono text-sm uppercase tracking-wider font-bold hover:shadow-2xl transition-all inline-flex items-center gap-3 group"
          >
            View All Projects
            <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;
